import React from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";

import { AuthProvider } from "@/context/AuthContext";
import { I18nProvider } from "@/lib/i18n";
import ProtectedRoute from "@/components/ProtectedRoute";
import AuthCallback from "@/components/AuthCallback";
import GoogleCallback from "@/components/GoogleCallback";
import Upgrade from "@/pages/Upgrade";
import FeatureGuard from "@/components/FeatureGuard";
import Activate from "@/pages/Activate";

import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import OnboardingRoute from "@/components/OnboardingRoute";
import Dashboard from "@/pages/Dashboard";
import Reservations from "@/pages/Reservations";
import Planning from "@/pages/Planning";
import Catalogue from "@/pages/Catalogue";
import Caisse from "@/pages/Caisse";
import Depenses from "@/pages/Depenses";
import Flotte from "@/pages/Flotte";
import FlotteDetail from "@/pages/FlotteDetail";
import Maintenance from "@/pages/Maintenance";
import Operations from "@/pages/Operations";
import Equipe from "@/pages/Equipe";
import EquipeDetail from "@/pages/EquipeDetail";
import Partenaires from "@/pages/Partenaires";
import PartenaireDetail from "@/pages/PartenaireDetail";
import Analytics from "@/pages/Analytics";
import AcceptInvite from "@/pages/AcceptInvite";
import Utilisateurs from "@/pages/Utilisateurs";
import Support from "@/pages/Support";
import Clients from "@/pages/Clients";
import AdminRoute from "@/components/AdminRoute";
import Placeholder from "@/pages/Placeholder";
import { NAV_ITEMS } from "@/components/layout/navConfig";

function AppRoutes() {
  const location = useLocation();
  // Handle Google OAuth return synchronously (before route guards run).
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/auth/google" element={<GoogleCallback />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/accept-invite" element={<AcceptInvite />} />
      <Route path="/activate" element={<Activate />} />
      <Route path="/dashboard-callback" element={<AuthCallback />} />
      <Route path="/onboarding" element={<OnboardingRoute />} />
      <Route path="/admin/*" element={<AdminRoute />} />

      <Route element={<ProtectedRoute />}>
        <Route path="/" element={<Dashboard />} />
        <Route path="/reservations" element={<Reservations />} />
        <Route path="/planning" element={<Planning />} />
        <Route path="/clients" element={<Clients />} />
        <Route path="/prestations" element={<Catalogue />} />
        <Route path="/caisse" element={<Caisse />} />
        <Route path="/depenses" element={<Depenses />} />
        <Route path="/upgrade" element={<Upgrade />} />
        <Route path="/parametres" element={<Utilisateurs />} />
        <Route path="/support" element={<Support />} />
        <Route element={<FeatureGuard feature="fleet" />}>
          <Route path="/flotte" element={<Flotte />} />
          <Route path="/flotte/:id" element={<FlotteDetail />} />
          <Route path="/maintenance" element={<Maintenance />} />
        </Route>
        <Route element={<FeatureGuard feature="team" />}>
          <Route path="/operations" element={<Operations />} />
          <Route path="/equipe" element={<Equipe />} />
          <Route path="/equipe/:id" element={<EquipeDetail />} />
        </Route>
        <Route element={<FeatureGuard feature="partners" />}>
          <Route path="/partenaires" element={<Partenaires />} />
          <Route path="/partenaires/:id" element={<PartenaireDetail />} />
        </Route>
        <Route element={<FeatureGuard feature="advanced_analytics" />}>
          <Route path="/analytics" element={<Analytics />} />
          <Route path="/statistiques" element={<Analytics />} />
        </Route>
        {NAV_ITEMS.filter((i) => !["/", "/reservations", "/planning", "/clients", "/prestations", "/caisse", "/depenses", "/flotte", "/maintenance", "/operations", "/equipe", "/partenaires", "/analytics", "/support"].includes(i.to)).map((i) => (
          <Route key={i.to} path={i.to} element={<Placeholder titleKey={i.key} icon={i.icon} />} />
        ))}
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <I18nProvider>
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </I18nProvider>
  );
}
